import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    RiFlashlightLine, RiLink, RiLockLine, RiServerLine,
} from "@remixicon/react";
import {
    ActionContextType,
    ArtificialIntelligenceAgentTool,
    FormType,
    GetArtificialIntelligenceObjectsDocument,
    McpServer,
    useDeleteMcpServerMutation,
    useGenerateMcpServerUrlMutation,
    useGetArtificialIntelligenceObjectsQuery,
    useGetWorkflowServicesQuery,
    useSaveMcpServerMutation,
} from "../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../api";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Toggle from "../../../../components/theme/toggle/Toggle";

import Avatar from "../../../../components/avatar/Avatar";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import PermissionsList, { PERMISSION_MAP } from "../../../../components/pickers/permissions/Permissions";
import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import { useDrawerForm } from "../../../../components/theme/drawer/Drawer";
import Icon from "../../../../components/theme/icon/Icon";
import SettingsContainer from "../../../../components/theme/settings";
import createTableContainer from "../../../../components/theme/table";
import Text from "../../../../components/theme/text/Text";
import usePortal from "../../../../contexts/PortalContext";
import Styles from "./ArtificialIntelligenceV3.module.scss";
import Badge from "../../../../components/theme/badge/Badge";
import Button from "../../../../components/theme/button/Button";
import { useConfirm } from "../../../../components/confirm/Confirm";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import { LoadingIcon } from "../../../../components/loading/Loading";

const TableContainer = createTableContainer<McpServer>();

export function useDelete() {
    const [deleteMCPServer] = useDeleteMcpServerMutation();

    return useDeleteConfirm<McpServer>(item => {
        deleteMCPServer({
            refetchQueries: [
                { query: GetArtificialIntelligenceObjectsDocument },
            ],
            variables: {
                id: item.id,
            },
        });
    }, agent => agent.name, "Delete Agent");
}
export function useCreate() {
    const [save] = useSaveMcpServerMutation();

    const [edit, setEdit] = useState<McpServer | undefined>(undefined);
    const { data: services, loading: isLoading } = useGetWorkflowServicesQuery();

    const servicesList = useMemo(
        () => services?.workflowServices.filter(row => row.installedIntegrationIds.isNotEmpty
                && row.actions?.some(action => action.contextTypes?.includes(ActionContextType.AiReadTool))),
        [services],
    );

    const { element, setOpen, formTracker } = useDrawerForm({
        title: "Create MCP Server",
        formTrackerBuilder: {
            handler: builder => builder
                .property<"id", string | undefined>("id", () => edit?.id)
                .property("name", () => edit?.name ?? "")
                .property("permissions", () => edit?.permissions)
                .property("availableActionsIds", () => edit?.availableActionsIds ?? [])
                .property<"availableTools", ArtificialIntelligenceAgentTool[]
            >("availableTools", () => edit?.availableTools ?? []),
            dependencies: [edit],
        },
        isLoading,
        onHide: () => {
            formTracker.reset();
            setEdit(undefined);
        },
        onSave: async () => {
            await save({
                refetchQueries: [
                    { query: GetArtificialIntelligenceObjectsDocument },
                ],
                variables: {
                    id: formTracker.properties.id.value,
                    settings: {
                        name: formTracker.properties.name.value,
                        permissions: formTracker.properties.permissions.value ?? [],
                        availableTools: cloneWithoutTypename(formTracker.properties.availableTools.value) ?? [],
                        availableActionsIds: formTracker.properties.availableActionsIds.value,
                    },
                },
            });
            setEdit(undefined);
            formTracker.reset();
        },

        body: tracker => (
            <>
                <FormGroup
                    label="Name"
                    size="medium"
                    required
                    type="text"
                    value={tracker.properties.name.value}
                    onChange={tracker.properties.name.setValueFromInput}
                />
                <FormGroup
                    label="Permissions"
                    size="medium"
                    type="text"
                >
                    <PermissionsList
                        value={tracker.properties.permissions.value}
                        onChange={tracker.properties.permissions.setValue}
                        isMulti
                        display="dropdown"
                        toggleText="Permissions"
                        icon={<RiLockLine />}
                        shouldDisplayAll
                    />
                </FormGroup>

                <FormGroup
                    label="Actions"
                    size="medium"
                    type="text"
                >
                    <RequestTypes
                        isMulti
                        type={FormType.Action}
                        toggleText="Actions"
                        icon={<RiFlashlightLine />}
                        value={tracker.properties.availableActionsIds.value}
                        onSelected={tracker.properties.availableActionsIds.setValue}
                    />
                </FormGroup>

                <div className={Styles["tools-list-container"]}>
                    <div className={Styles["tools-item-header"]}>
                        <Text as="h3" textVariant="body-1-semibold">Available Tools</Text>
                        <Toggle
                            checked={tracker.properties.availableTools.value.length === servicesList?.length}
                            onChange={event => {
                                tracker.properties.availableTools.setValue(event.target.checked
                                    ? [...(servicesList?.map(service => ({ serviceId: service.id })) ?? [])]
                                    : []);
                            }}
                        />
                    </div>
                    <div className={Styles["tools-item-list"]}>

                        {servicesList?.map(service => (
                            <div key={service.id} className={Styles["tools-item"]}>
                                <Avatar src={service.squareIconUrl as string} variant="xxs" isCircle />
                                <Text
                                    textVariant="body-1-regular"
                                    className={Styles["tools-item-text"]}
                                >
                                    {service.name}
                                </Text>
                                <Toggle
                                    checked={tracker.properties.availableTools.value.some(row => row.serviceId === service.id)}
                                    onChange={event => {
                                        tracker.properties.availableTools.setValue(event.target.checked
                                            ? [...tracker.properties.availableTools.value, {
                                                serviceId: service.id,
                                            }]
                                            : tracker.properties.availableTools.value
                                                .filter(value => value.serviceId !== service.id));
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

            </>
        ),
    });

    return {
        element,
        setOpen,
        setEdit: (current: McpServer) => {
            setOpen(true);
            setEdit(current);
        },
    };
}

export function useCreateLink() {
    const [url, setUrl] = useState<string>();

    const [generateUrl] = useGenerateMcpServerUrlMutation();

    const { element, setShouldShow } = useConfirm({
        title: "Get MCP Server URL",
        body: url ? (
            <div>
                <CodeBlock code={url} />
            </div>
        ) : <LoadingIcon />,
    });

    return {
        element,
        setShow: async (item: McpServer) => {
            setShouldShow(true);
            const data = await generateUrl({
                variables: {
                    id: item.id,
                },
            });
            setUrl(data.data?.generateMCPServerUrl.url);
        },
    };
}
export default function MCPServers({
    isEnabled,
}: {
    isEnabled: boolean
}) {
    const { entitlementIds } = usePortal();

    const { data: agentsData, loading: isLoading } = useGetArtificialIntelligenceObjectsQuery({
        variables: {
            inclueAgents: entitlementIds.has(entitlementIds.keys.ARTIFICIAL_INTELLIGENCE),
            includeMCPServers: entitlementIds.has(entitlementIds.keys.MCP_SERVERS),
        },
    });
    const { element, setOpen: setCreateAgentOpen, setEdit: setEditAgent } = useCreate();
    const { element: deleteElement, setDeleting } = useDelete();
    const { element: createElementUrl, setShow: setShowCreateUrl } = useCreateLink();

    return (
        <SettingsContainer.Section
            title="MCP Servers"
            helpText="MCP Servers allow you to connect your AI agents to Foqal. Create tickets, manage requests, or automate
                your workflows from any AI tools."
        >
            <SettingsContainer.Setting
                title="MCP Servers"
                description="MCP Servers allow you to connect your AI agents to Foqal.
                    Create tickets, manage requests, or automate your workflows from any AI tools."
                permissions={SettingsContainer.Setting.Permissions.Administrator}
                isHideTitle
                isVisible={isEnabled}
            >
                <TableContainer
                    data={agentsData?.mcpServers}
                    isLoading={isLoading}
                    borderVariant="secondary"
                >
                    <TableContainer.Empty>
                        <TableContainer.Empty.Default
                            text="MCP Servers allow you to connect your AI agents to Foqal.
                                Create tickets, manage requests, or automate your workflows from any AI tools."
                            title="No MCP Servers"
                            icon={<RiServerLine />}
                            onClick={() => {
                                setCreateAgentOpen(true);
                            }}
                            buttonText="Create MCP Server"
                        />
                    </TableContainer.Empty>
                    <TableContainer.TableControls>
                        <TableContainer.TableControls.Search
                            search={(query, item) => item.name?.toLowerCase().includes(query)
                        || item.permissions.some(row => row.toLowerCase().includes(query))}
                            autoFocus={false}
                        />
                        <TableContainer.TableControls.Seperator />

                        <TableContainer.TableControls.Create
                            text="Create MCP Server"
                            onClick={() => {
                                setCreateAgentOpen(true);
                            }}
                        />
                    </TableContainer.TableControls>
                    <TableContainer.Table>

                        <TableContainer.TableValue
                            columnKey="name"
                            title="Name"
                        >
                            {item => item.name}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="permissions"
                            title="# of Permissions"
                        >
                            {item => (
                                <Badge
                                    variant="info"
                                    prefixIcon={<Icon icon={<RiLockLine />} variant="secondary" />}
                                >
                                    {item.permissions.length}
                                </Badge>
                            )}
                        </TableContainer.TableValue>

                        <TableContainer.TableValue
                            columnKey="actions"
                            title="# of Actions"
                        >
                            {item => (
                                <Badge
                                    variant="info"
                                    prefixIcon={<Icon icon={<RiFlashlightLine />} variant="secondary" />}
                                >
                                    {item.availableActionsIds.length}
                                </Badge>
                            )}
                        </TableContainer.TableValue>

                        <TableContainer.TableValue
                            columnKey="tools"
                            title="# of Tools"
                        >
                            {item => (
                                <Badge
                                    variant="info"
                                    prefixIcon={<Icon.Integrations variant="secondary" />}
                                >
                                    {item.availableTools.length}
                                </Badge>
                            )}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="getLink"
                            title="Server URL"
                        >
                            {item => (
                                <Button
                                    variant="ghost-outline-blue"
                                    className={Styles["workflow-button"]}
                                    title={`Workflow for ${item.id}`}
                                    size="xsmall"
                                    prefixIcon={<RiLink />}
                                    onClick={() => {
                                        setShowCreateUrl(item);
                                    }}
                                >
                                    Get Server URL
                                </Button>
                            )}
                        </TableContainer.TableValue>

                        <TableContainer.Action
                            icon={<Icon.Edit />}
                            altText="Edit"
                            onClick={item => {
                                setEditAgent(item);
                            }}
                        />
                        <TableContainer.Action
                            icon={<Icon.Delete />}
                            altText="Delete"
                            onClick={item => {
                                setDeleting(item);
                            }}
                        />

                    </TableContainer.Table>
                </TableContainer>
            </SettingsContainer.Setting>

            {element}
            {deleteElement}
            {createElementUrl}

        </SettingsContainer.Section>
    );
}
